import React, { useEffect, useState, useRef } from 'react';
import { SystemSettings } from '../types';
import { CryptoLogos } from '../constants';
import { Send, Instagram, Menu, X } from 'lucide-react';

interface LandingPageProps {
  onGetStarted: () => void;
  onLogin: () => void;
  onAboutClick: () => void;
  systemSettings: SystemSettings | null;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onLogin, onAboutClick, systemSettings }) => {
  const [scrollPos, setScrollPos] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [marketData, setMarketData] = useState<{ gainers: any[], losers: any[] }>({ gainers: [], losers: [] });
  const [activeMarketTab, setActiveMarketTab] = useState<'gainers' | 'losers'>('gainers');

  useEffect(() => {
    const handleScroll = () => setScrollPos(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    const fetchMarketData = async () => {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&sparkline=false&price_change_percentage=24h');
        const data = await res.json();
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => b.price_change_percentage_24h_in_currency - a.price_change_percentage_24h_in_currency);
          setMarketData({
            gainers: sorted.slice(0, 5),
            losers: [...data].sort((a, b) => a.price_change_percentage_24h_in_currency - b.price_change_percentage_24h_in_currency).slice(0, 5)
          });
        }
      } catch (err) {
        console.error("Market data sync failed", err);
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 60000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  const siteName = systemSettings?.siteName || 'BTCC-AI Trading';

  return (
    <div className="min-h-screen bg-bg overflow-x-hidden selection:bg-primary/30 text-white font-sans grid-bg">
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Futuristic Navbar */}
      <nav className={`fixed top-0 left-0 w-full z-[100] px-6 md:px-16 py-8 flex items-center justify-between transition-all duration-700 ${scrollPos > 50 ? 'bg-bg/40 backdrop-blur-2xl py-5 border-b border-border' : 'bg-transparent'}`}>
        <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <span className="text-black font-black text-2xl">{siteName[0]}</span>
          </div>
          <span className="font-black text-2xl tracking-tighter text-white uppercase">{siteName}</span>
        </div>
        
        <div className="hidden lg:flex items-center gap-16">
          <button onClick={onAboutClick} className="text-[10px] font-black uppercase tracking-[0.4em] text-white/30 hover:text-[#AEB877] transition-all hover:translate-y-[-2px] relative group">
            About Us
            <span className="absolute -bottom-2 left-0 w-0 h-[2px] bg-[#AEB877] group-hover:w-full transition-all duration-500 shadow-[0_0_10px_#AEB877]" />
          </button>
          {[
            { label: 'Ecosystem', id: 'ecosystem' },
            { label: 'Protocol', id: 'protocol' },
            { label: 'Neural Core', id: 'neural-core' },
            { label: 'Security', id: 'security' }
          ].map((item) => (
            <a key={item.id} href={`#${item.id}`} className="text-[10px] font-black uppercase tracking-[0.4em] text-white/30 hover:text-[#AEB877] transition-all hover:translate-y-[-2px] relative group">
              {item.label}
              <span className="absolute -bottom-2 left-0 w-0 h-[2px] bg-[#AEB877] group-hover:w-full transition-all duration-500 shadow-[0_0_10px_#AEB877]" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4 md:gap-8">
          <button onClick={onGetStarted} className="primary-button hidden sm:block">Get Started</button>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-white/60 hover:text-white transition-colors"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[90] lg:hidden transition-all duration-500 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-bg/95 backdrop-blur-2xl" onClick={() => setIsMenuOpen(false)} />
        <div className={`absolute top-0 right-0 w-full h-full max-w-sm bg-surface border-l border-border p-12 flex flex-col gap-12 transition-transform duration-500 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col gap-8 mt-24">
            <button 
              onClick={() => { onAboutClick(); setIsMenuOpen(false); }} 
              className="text-left text-xs font-black uppercase tracking-[0.3em] text-white/40 hover:text-primary transition-all"
            >
              About Us
            </button>
            {[
              { label: 'Ecosystem', id: 'ecosystem' },
              { label: 'Protocol', id: 'protocol' },
              { label: 'Neural Core', id: 'neural-core' },
              { label: 'Security', id: 'security' }
            ].map((item) => (
              <a 
                key={item.id} 
                href={`#${item.id}`} 
                onClick={() => setIsMenuOpen(false)}
                className="text-xs font-black uppercase tracking-[0.3em] text-white/40 hover:text-primary transition-all"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="mt-auto space-y-6">
            <button onClick={() => { onGetStarted(); setIsMenuOpen(false); }} className="w-full primary-button py-4 text-xs">Get Started</button>
            <div className="flex gap-8 justify-center pt-8 border-t border-white/5">
              <a href={systemSettings?.telegramLink || "#"} target="_blank" rel="noreferrer" className="text-white/20 hover:text-primary transition-all">
                <Send size={24} />
              </a>
              <a href={systemSettings?.instagramLink || "#"} target="_blank" rel="noreferrer" className="text-white/20 hover:text-primary transition-all">
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hero: Minimalist Futuristic */}
      <section className="relative pt-24 md:pt-32 lg:pt-40 pb-16 px-6 z-10">
        <div className="max-w-7xl mx-auto text-center space-y-12">
          <div className="inline-flex items-center gap-4 px-8 py-3 rounded-full border border-border bg-white/5 text-white/60 text-[10px] font-black uppercase tracking-[0.4em] animate-in slide-in-from-bottom duration-1000">
             <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_var(--color-primary-glow)] animate-pulse" />
             System Status: Ready Go Execution
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black leading-[0.8] tracking-tighter uppercase animate-in zoom-in-95 duration-1000">
           
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">{siteName}.</span>
          </h1>
          
          <p className="max-w-2xl mx-auto text-muted text-base md:text-xl font-bold uppercase tracking-wider leading-relaxed animate-in slide-in-from-bottom-4 duration-1000">
            Autonomous neural architecture executing high-frequency delta strategies with 4ms latency. Next-gen capital allocation for the digital age.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-12 pt-10">
            <button onClick={onGetStarted} className="w-full sm:w-auto bg-white text-black px-16 py-8 rounded-[32px] font-black text-2xl hover:bg-primary transition-all shadow-2xl uppercase tracking-tighter active:scale-95">
              Initialize Robot
            </button>
            <div className="flex flex-col items-start">
              <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em] mb-2">Network Uptime</span>
              <span className="text-3xl font-black text-white glow-text">99.998%</span>
            </div>
          </div>

          {/* Market Pulse Section */}
          <div className="max-w-4xl mx-auto mt-20 animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="glass-card p-1 overflow-hidden">
              <div className="flex border-b border-white/5">
                <button 
                  onClick={() => setActiveMarketTab('gainers')}
                  className={`flex-1 py-4 text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeMarketTab === 'gainers' ? 'bg-white/5 text-[#AEB877]' : 'text-white/20 hover:text-white'}`}
                >
                  Top Gainers
                </button>
                <button 
                  onClick={() => setActiveMarketTab('losers')}
                  className={`flex-1 py-4 text-[10px] font-black uppercase tracking-[0.3em] transition-all ${activeMarketTab === 'losers' ? 'bg-white/5 text-[#FF4D4D]' : 'text-white/20 hover:text-white'}`}
                >
                  Top Losers
                </button>
              </div>
              <div className="p-6 space-y-4">
                {(activeMarketTab === 'gainers' ? marketData.gainers : marketData.losers).map((coin, idx) => (
                  <div key={coin.id} className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all group">
                    <div className="flex items-center gap-4">
                      <span className="text-[10px] font-black text-white/10 w-4">{idx + 1}</span>
                      <img src={coin.image} alt={coin.name} className="w-8 h-8 rounded-full grayscale group-hover:grayscale-0 transition-all" referrerPolicy="no-referrer" />
                      <div>
                        <p className="text-sm font-black text-white uppercase tracking-tight">{coin.symbol}</p>
                        <p className="text-[8px] font-black text-white/20 uppercase tracking-widest">{coin.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-white tabular-nums">${coin.current_price.toLocaleString()}</p>
                      <p className={`text-[10px] font-black tabular-nums ${coin.price_change_percentage_24h >= 0 ? 'text-[#AEB877]' : 'text-[#FF4D4D]'}`}>
                        {coin.price_change_percentage_24h >= 0 ? '+' : ''}{coin.price_change_percentage_24h.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                ))}
                {(!marketData.gainers.length) && (
                  <div className="py-12 text-center">
                    <div className="w-8 h-8 border-2 border-[#AEB877] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-[8px] font-black text-white/20 uppercase tracking-widest">Syncing Global Exchanges...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grids: Minimalist Glass */}
      <section id="ecosystem" className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: 'Latency', value: '4.2ms', desc: 'Sub-millisecond processing for lightning entry.' },
              { label: 'Scalability', value: '$840M+', desc: 'Infrastructure built for high-volume liquidity.' },
              { label: 'Security', value: 'ECC-512', desc: 'Military-grade node encryption protocols.' },
              { label: 'Uptime', value: '99.99%', desc: 'Distributed cross-continent relay network.' }
            ].map((stat, i) => (
              <div key={i} className="glass-card p-12 group">
                <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em] mb-6 group-hover:text-[#AEB877] transition-colors">{stat.label}</p>
                <p className="text-5xl font-black mb-6 tracking-tighter">{stat.value}</p>
                <p className="text-[11px] font-bold text-white/40 uppercase tracking-widest leading-relaxed">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us: Informative Section */}
      <section id="protocol" className="py-16 px-6 border-y border-border/50 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
             <h2 className="text-3xl md:text-6xl font-black tracking-tighter uppercase break-words">The Protocol Advantage</h2>
             <p className="text-white/20 font-black uppercase tracking-[0.5em] text-xs">Engineered for absolute market dominance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
             {[
               { title: 'Neural Execution', desc: 'Our proprietary neural core processes millions of data points per second to execute trades with surgical precision.' },
               { title: 'Global Liquidity', desc: 'Direct integration with top-tier exchanges ensures deep liquidity and minimal slippage for all executions.' },
               { title: 'Risk Management', desc: 'Advanced algorithmic risk controls protect your capital against extreme market volatility in real-time.' }
             ].map((item, i) => (
               <div key={i} className="space-y-6">
                  <div className="w-12 h-12 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center text-primary font-black text-xl">0{i+1}</div>
                  <h3 className="text-2xl font-black uppercase tracking-tighter">{item.title}</h3>
                  <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">{item.desc}</p>
               </div>
             ))}
          </div>
        </div>
      </section>

      {/* Animated Crypto Pairs Marquee */}
      <section className="py-20 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-r from-bg via-transparent to-bg z-10 pointer-events-none" />
        
        <div className="space-y-8">
          {/* Row 1 */}
          <div className="flex whitespace-nowrap animate-marquee">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-6 px-3">
                {[
                  { name: 'API3/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'APT/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'ARB/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'ARC/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'ARDR/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'ARKM/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">A</div> },
                  { name: 'ETH/USDT', icon: <div className="w-6 h-6"><CryptoLogos.ETH /></div> },
                  { name: 'BTC/USDT', icon: <div className="w-6 h-6"><CryptoLogos.BTC /></div> },
                  { name: 'SOL/USDT', icon: <div className="w-6 h-6"><CryptoLogos.SOL /></div> },
                  { name: 'BNB/USDT', icon: <div className="w-6 h-6"><CryptoLogos.BNB /></div> },
                  { name: 'TRX/USDT', icon: <div className="w-6 h-6"><CryptoLogos.TRX /></div> },
                  { name: 'XRP/USDT', icon: <div className="w-6 h-6"><CryptoLogos.XRP /></div> },
                ].map((pair, j) => (
                  <div key={j} className="flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 bg-white/[0.02] backdrop-blur-sm hover:border-primary/50 transition-colors group cursor-default">
                    <div className="w-6 h-6 grayscale group-hover:grayscale-0 transition-all">
                      {pair.icon}
                    </div>
                    <span className="text-xs font-black text-white/60 group-hover:text-white uppercase tracking-tighter">{pair.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Row 2 */}
          <div className="flex whitespace-nowrap animate-marquee-reverse">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-6 px-3">
                {[
                  { name: 'MOODENG/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">M</div> },
                  { name: 'MOON/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">M</div> },
                  { name: 'MOVE/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">M</div> },
                  { name: 'M/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">M</div> },
                  { name: 'MYX/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">M</div> },
                  { name: 'NEAR/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">N</div> },
                  { name: 'SYN/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">S</div> },
                  { name: 'SYRUP/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">S</div> },
                  { name: 'TAC/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">T</div> },
                  { name: 'TAO/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">T</div> },
                  { name: 'TIA/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">T</div> },
                  { name: 'TKO/USDT', icon: <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[8px]">T</div> },
                ].map((pair, j) => (
                  <div key={j} className="flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 bg-white/[0.02] backdrop-blur-sm hover:border-primary/50 transition-colors group cursor-default">
                    <div className="w-6 h-6 grayscale group-hover:grayscale-0 transition-all">
                      {pair.icon}
                    </div>
                    <span className="text-xs font-black text-white/60 group-hover:text-white uppercase tracking-tighter">{pair.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Neural Integration: Split Layout */}
      <section id="neural-core" className="py-24 px-6 relative">
         <div className="max-w-7xl mx-auto flex flex-col lg:flex-row-reverse items-center gap-24">
            <div className="lg:w-1/2 space-y-10">
               <h2 className="text-3xl md:text-6xl font-black tracking-tighter uppercase leading-[0.85]">Autonomous <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#AEB877] to-white">Capital Logic.</span></h2>
               <p className="text-white/40 text-lg font-bold uppercase leading-relaxed tracking-wider">Our AI doesn't follow trends; it identifies liquidity voids. By analyzing Orderflow and Volume Profile in real-time, {siteName} targets high-probability reversal points.</p>
               <div className="grid grid-cols-1 gap-6">
                  {[
                    { title: 'Liquidity Void Detection', icon: '🌀' },
                    { title: 'HFT Execution Engine', icon: '⚔️' },
                    { title: 'Dynamic Risk Guard', icon: '🛡️' }
                  ].map((feat, i) => (
                    <div key={i} className="flex items-center gap-8 p-8 glass-card group">
                      <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-2xl group-hover:bg-[#AEB877]/10 group-hover:text-[#AEB877] transition-all">{feat.icon}</div>
                      <span className="text-lg font-black uppercase tracking-widest">{feat.title}</span>
                    </div>
                  ))}
               </div>
            </div>
            <div className="lg:w-1/2 relative">
               <div className="absolute inset-0 bg-[#AEB877]/20 blur-[120px] rounded-full animate-pulse" />
               <div className="relative rounded-[60px] overflow-hidden border border-white/10 shadow-2xl">
                  <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1000" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000" alt="Robotics" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
               </div>
            </div>
         </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-16 px-6">
         <div className="max-w-7xl mx-auto glass-card p-12 md:p-20 flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/3">
               <div className="w-32 h-32 bg-primary/10 border border-primary/20 rounded-[40px] flex items-center justify-center text-primary">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
               </div>
            </div>
            <div className="md:w-2/3 space-y-8">
               <h2 className="text-3xl md:text-6xl font-black tracking-tighter uppercase">Hardened Security</h2>
               <p className="text-muted text-lg font-bold uppercase tracking-widest leading-relaxed">Your assets are protected by multi-signature cold storage and real-time threat detection. We employ military-grade encryption to ensure your node remains impenetrable.</p>
               <div className="flex flex-wrap gap-4">
                  {['AES-256', '2FA_ENFORCED', 'COLD_STORAGE', 'DDOS_PROTECTION'].map(tag => (
                    <span key={tag} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-[10px] font-black uppercase tracking-widest text-white/40">{tag}</span>
                  ))}
               </div>
            </div>
         </div>
      </section>

      {/* CTA Final Sequence */}
      <section className="py-12 md:py-24 px-6">
        <div className="max-w-6xl mx-auto text-center relative p-8 md:p-24 rounded-[40px] md:rounded-[60px] border border-white/10 overflow-hidden bg-black/40 backdrop-blur-3xl">
          <div className="absolute top-0 left-0 w-full h-full opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <h2 className="text-3xl md:text-7xl font-black tracking-tighter uppercase mb-8 md:mb-12 relative z-10 leading-[0.9] md:leading-[0.8]">
            Ready for <br />
            <span className="text-[#AEB877]">Deployment?</span>
          </h2>
          <p className="text-white/30 font-black uppercase tracking-[0.3em] md:tracking-[0.5em] text-[10px] md:text-lg mb-10 md:mb-16 relative z-10 max-w-xs md:max-w-none mx-auto">Secure your node in the global alpha cluster today.</p>
          <button onClick={onGetStarted} className="relative z-10 bg-white text-black px-8 py-4 md:px-16 md:py-8 rounded-2xl md:rounded-[32px] font-black text-sm md:text-xl hover:bg-[#AEB877] transition-all shadow-2xl uppercase tracking-tighter active:scale-95 mx-auto">
            Join the Network
          </button>
        </div>
      </section>

      {/* Global Footer */}
      <footer className="py-16 px-6 border-t border-border relative z-10 bg-bg">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-16">
          <div className="md:col-span-2 space-y-12">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <span className="text-black font-black text-2xl">{siteName[0]}</span>
              </div>
              <span className="font-black text-2xl tracking-tighter text-white uppercase">{siteName}</span>
            </div>
            <p className="max-w-md text-muted text-xs font-bold uppercase tracking-widest leading-relaxed">
              Industrial grade artificial intelligence for autonomous capital markets. Distributed, secure, and relentless. Authorized across 42 global jurisdictions.
            </p>
          </div>
          
          <div className="space-y-8">
             <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.5em]">Command Hub</h4>
             <p className="text-[11px] text-white/20 font-bold uppercase tracking-widest leading-loose">
                {systemSettings?.officeAddress || 'Global Operations Hub'}<br />
                {systemSettings?.siteEmail || 'hq@neural.net'}<br />
                {systemSettings?.phoneNumber || 'Signal: Secure'}
             </p>
          </div>

          <div className="flex flex-col items-start md:items-end justify-between">
             <div className="flex gap-6">
                <a 
                  href={systemSettings?.telegramLink || "#"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-white/20 hover:text-[#AEB877] transition-all flex items-center gap-2 group"
                >
                  <Send size={16} className="group-hover:scale-110 transition-transform" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Telegram</span>
                </a>
                <a 
                  href={systemSettings?.instagramLink || "#"} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-white/20 hover:text-[#AEB877] transition-all flex items-center gap-2 group"
                >
                  <Instagram size={16} className="group-hover:scale-110 transition-transform" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">Instagram</span>
                </a>
             </div>
             <p className="text-white/10 text-[9px] font-black uppercase tracking-[0.4em] mt-16 md:text-right">
                © 2026 {siteName.toUpperCase()} SYSTEMS. <br />
                ALL NEURAL PATHWAYS RESERVED.
             </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;